import React, { forwardRef } from 'react'
import { cva, cx, type VariantProps } from 'class-variance-authority'
import Paragraph from './Paragraph'
import Icon from './Icon'
import { AlertCircle } from 'lucide-react'

const textareaStyles = cva(
    [
        'w-full bg-white border-0.5 rounded-2 resize-none',
        'focus:outline-hidden focus:ring-[3px] focus:ring-opacity-100',
        'transition-shadow duration-150 ease-in-out',
        'text-sm',
        'placeholder:text-textPlaceholder',
        'disabled:text-textDisabled disabled:bg-backgroundDisabled',
        'py-3 px-3'
    ],
    {
        variants: {
            state: {
                default: ['border-borderPrimary', 'focus:border-borderTertiary focus:ring-borderEmphasis'],
                complete: ['border-borderPrimary', 'focus:border-borderTertiary focus:ring-borderEmphasis'],
                error: [
                    'text-textNegative',
                    'border-borderNegativeSubdued',
                    'focus:border-borderNegativeSubdued focus:ring-borderEmphasisNegative'
                ]
            }
        },
        defaultVariants: {
            state: 'default'
        }
    }
)

export interface TextAreaProps
    extends React.TextareaHTMLAttributes<HTMLTextAreaElement>, VariantProps<typeof textareaStyles> {
    label?: string
    required?: boolean
    message?: string
    rows?: number
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    ({ className, label, required, message, state = 'default', rows = 4, ...props }, ref) => {
        return (
            <div className="w-full" data-testid="__textarea-wrapper__">
                {label && (
                    <label
                        htmlFor={props.name}
                        className="block text-sm font-medium text-textPrimary mb-2"
                        data-testid="__textarea-label__"
                    >
                        {label}
                        {required && (
                            <span className="text-textNegative ml-1" data-testid="__textarea-required-indicator__">
                                *
                            </span>
                        )}
                    </label>
                )}
                <div className="relative">
                    <textarea
                        id={props.name}
                        className={cx(textareaStyles({ state }), className)}
                        ref={ref}
                        rows={rows}
                        data-testid="__textarea-field__"
                        {...props}
                    />
                    {state === 'error' && (
                        <div className="absolute top-2 right-2" data-testid="__error-icon__">
                            <Icon icon={AlertCircle} size="1" color="negative" />
                        </div>
                    )}
                </div>
                {message && (
                    <Paragraph
                        className="pt-2"
                        size={3}
                        color={state === 'error' ? 'negative' : undefined}
                        data-testid="__textarea-message__"
                    >
                        {message}
                    </Paragraph>
                )}
            </div>
        )
    }
)

export default TextArea
